import { useState, useEffect } from "react";
import {
  Title,
  Space,
  Box,
  Button,
  Text,
  Modal,
  Grid,
  Flex,
} from "@mantine/core";
import { useLazyQuery } from "@apollo/client";
import JSZip from "jszip";

import BoxOrderView from "./BoxOrderView";
import ItemsDescription from "./item-description/itemDescription";
import TranslateCommentModal from "./modals/translateCommentModal";
import { STYLE } from "../types/constants";
import {
  File as SharedFile,
  Order,
  Product,
  User,
  OrderComment,
} from "../types/interfaces";
import { ItemType, FileType, UserType, ImprintType } from "../types/enums";
import { isUserStaff } from "../utils/user.utils";
import { joinSocketRoom, onSocketEvent } from "../socket";

import { GET_FILE_DOWNLOAD_URL } from "../gql/files";
import DoctorNotes from "./DoctorNotes";
import OrderFilesDisplay from "./OrderFilesDisplay";

interface OrderViewOrderDetailProps {
  order: Order;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  user: User;
  refetch: () => void;
  fileWithPath: SharedFile[];
  initialOrderComments: any[];
  setPictureToMarkAsRead: (picture: any) => void;
  id: string;
}

const OrderViewOrderDetail = ({
  order,
  selectedProduct,
  setSelectedProduct,
  user,
  refetch,
  fileWithPath,
  initialOrderComments,
  setPictureToMarkAsRead,
  id,
}: OrderViewOrderDetailProps) => {
  const [openTranslationModal, setOpenTranslationModal] = useState(false);
  const [commentToTranslate, setCommentToTranslate] = useState<any>(null);
  const [orderCommentList, setOrderCommentList] = useState<OrderComment[]>(
    initialOrderComments ?? []
  );

  const [getFileDownloadUrl] = useLazyQuery(GET_FILE_DOWNLOAD_URL);

  const getPresignedDownloadUrl = async (key: string) => {
    const res = await getFileDownloadUrl({ variables: { key } });
    window.open(res?.data?.getPresignedFileDownloadUrl, "_blank");
  };

  useEffect(() => {
    const leaveHelpRoom = joinSocketRoom(`help-${order.id}`);
    const offHelpMessage = onSocketEvent("helpMessage", () => refetch());

    return () => {
      offHelpMessage();
      leaveHelpRoom();
    };
  }, [order.id, refetch]);

  useEffect(() => {
    setOrderCommentList(initialOrderComments);
  }, [initialOrderComments]);

  return (
    <>
      <Modal
        opened={openTranslationModal}
        onClose={() => setOpenTranslationModal(false)}
      >
        <TranslateCommentModal
          order={order}
          closeModal={() => setOpenTranslationModal(false)}
          refetch={refetch}
          comment={commentToTranslate}
        />
      </Modal>
      <BoxOrderView id={id} span={7} minHeight={54}>
        <Title
          order={4}
          style={{
            color: STYLE.primary,
          }}
        >
          {`Order detail${
            order?.products && order?.products.length > 1
              ? " (clic on a product to select it)"
              : ""
          }`}
        </Title>
        <Box>
          <Space h="md" />
          <Box>
            {order?.products &&
              (order.products ?? []).map((product: Product) => (
                <Box key={product.id}>
                  <Button
                    variant={
                      selectedProduct?.id === product.id ? "filled" : "outline"
                    }
                    color={
                      selectedProduct?.id === product.id
                        ? STYLE.primary
                        : "black"
                    }
                    styles={{
                      inner: { justifyContent: "start" },
                    }}
                    h="auto"
                    p={8}
                    mb="xs"
                    w="100%"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <ItemsDescription
                      key={product.id}
                      product={product}
                      order={order}
                      shouldTranslate={true}
                      showProductCreator
                    />
                  </Button>
                  {isUserStaff(user) &&
                    selectedProduct &&
                    selectedProduct.id === product.id &&
                    selectedProduct?.removableItem &&
                    selectedProduct.removableItem[0] &&
                    selectedProduct.removableItem[0].itemType ===
                      ItemType.PARTIAL_DENTURE &&
                    selectedProduct.removableItem[0].dentistNotes && (
                      <>
                        <Text size="sm">
                          {selectedProduct.removableItem[0].dentistNotes !== ""
                            ? `Commentaire sur les crochets: ${selectedProduct.removableItem[0].dentistNotes}`
                            : "Commentaire sur les crochets: Aucun"}
                        </Text>
                        <Space h="sm" />
                      </>
                    )}
                </Box>
              ))}
          </Box>
          <Space h="xl" />
          <DoctorNotes
            user={user}
            orderCommentList={orderCommentList}
            orderStatus={order.status}
            setCommentToTranslate={setCommentToTranslate}
            setOpenTranslationModal={setOpenTranslationModal}
            getPresignedDownloadUrl={getPresignedDownloadUrl}
          />
        </Box>
        <OrderFilesDisplay
          order={order}
          user={user}
          fileWithPath={fileWithPath}
          refetch={refetch}
          setPictureToMarkAsRead={setPictureToMarkAsRead}
        />
      </BoxOrderView>
    </>
  );
};

export default OrderViewOrderDetail;
